import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Send, Loader2, Copy, Check, Mail, MessageSquare, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const input = "w-full rounded-sm bg-navy border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold transition-shadow duration-200";
const label = "block text-xs uppercase tracking-wider text-slate-400 mb-1.5";

export function PaymentRequestDialog() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState("");
  const [form, setForm] = useState({ amount: "", description: "", name: "", phone: "", email: "", payment_method: "online" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const reset = () => { setForm({ amount: "", description: "", name: "", phone: "", email: "", payment_method: "online" }); setResult(null); };
  const copy = (t) => { navigator.clipboard.writeText(t); setCopied(t); toast.success("Copied!"); setTimeout(() => setCopied(""), 1500); };

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    if (!form.description.trim()) { toast.error("Add a short description (what it's for)."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/invoices", {
        name: form.name.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        payment_method: form.payment_method,
        items: [{ description: form.description.trim(), amount: amt }],
      });
      setResult(data);
      toast.success("Payment request created!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create request.");
    } finally {
      setLoading(false);
    }
  };

  const payUrl = result?.pay_url || "";
  const msg = result
    ? `Hi${form.name ? " " + form.name : ""}, here is your secure payment link for ${form.description} — $${result.amount.toFixed(2)}: ${payUrl}`
    : "";
  const mailto = `mailto:${form.email || ""}?subject=${encodeURIComponent("Your payment link — Bridge Taxi")}&body=${encodeURIComponent(msg)}`;
  const sms = `sms:${form.phone || ""}?&body=${encodeURIComponent(msg)}`;

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button data-testid="payment-request-btn" className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-navy hover:bg-gold-light transition-colors duration-200">
          <DollarSign className="h-4 w-4" /> Payment request
        </button>
      </DialogTrigger>
      <DialogContent className="bg-navy-light border border-gold/30 text-white max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white font-heading">Custom payment request</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">Enter an agreed fare and generate a secure payment link to send your client by email or text.</DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Amount (CAD)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gold">$</span>
                  <input data-testid="pr-amount" type="number" min="1" step="0.01" value={form.amount} onChange={set("amount")} placeholder="0.00" className={`${input} pl-7`} />
                </div>
              </div>
              <div>
                <label className={label}>Payment method</label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="pr-method" className="bg-navy border-gold/20 text-white h-[46px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="online">Card (+HST 15%)</SelectItem>
                    <SelectItem value="e_transfer">Interac e-Transfer</SelectItem>
                    <SelectItem value="pay_later">Pay on arrival</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className={label}>What's it for?</label>
              <input data-testid="pr-desc" value={form.description} onChange={set("description")} placeholder="e.g. Airport transfer — Aug 20" className={input} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>Customer name <span className="text-slate-600">(optional)</span></label>
                <input data-testid="pr-name" value={form.name} onChange={set("name")} placeholder="Name" className={input} />
              </div>
              <div>
                <label className={label}>Phone <span className="text-slate-600">(optional)</span></label>
                <input data-testid="pr-phone" value={form.phone} onChange={set("phone")} placeholder="For SMS" className={input} />
              </div>
            </div>
            <div>
              <label className={label}>Email <span className="text-slate-600">(optional)</span></label>
              <input data-testid="pr-email" value={form.email} onChange={set("email")} placeholder="For emailing the link" className={input} />
            </div>
            <button onClick={submit} disabled={loading} data-testid="pr-create-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold py-3 font-semibold text-navy hover:bg-gold-light transition-colors duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Generate payment link
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="pr-result">
            <div className="rounded-sm bg-navy border border-gold/25 p-4 text-center">
              <div className="text-xs uppercase tracking-wider text-slate-400">Amount to pay</div>
              <div className="font-heading text-3xl font-bold text-gold mt-1">${result.amount.toFixed(2)}</div>
              {result.tax_amount > 0 && <div className="text-xs text-slate-500 mt-1">incl. HST ${result.tax_amount.toFixed(2)}</div>}
            </div>
            <div>
              <label className={label}>Secure payment link</label>
              <div className="flex gap-2">
                <input readOnly value={payUrl} data-testid="pr-pay-url" className={`${input} text-gold`} />
                <button onClick={() => copy(payUrl)} className="shrink-0 rounded-sm border border-gold/40 p-3 text-gold hover:bg-gold/10">{copied === payUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <a href={mailto} data-testid="pr-send-email" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 py-3 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><Mail className="h-4 w-4" /> Send by Email</a>
              <a href={sms} data-testid="pr-send-sms" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 py-3 text-sm font-semibold text-gold hover:bg-gold/10 transition-colors duration-200"><MessageSquare className="h-4 w-4" /> Send by Text</a>
            </div>
            <a href={`/invoice/${result.id}`} target="_blank" rel="noopener noreferrer" data-testid="pr-invoice-url" className="block text-center text-xs text-slate-400 hover:text-gold">View / print invoice →</a>
            <button onClick={reset} data-testid="pr-another" className="w-full rounded-full border border-gold/30 py-2.5 text-sm text-slate-200 hover:border-gold hover:text-gold transition-colors duration-200">Create another</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
